import {
  Castor,
  Collection,
  CollectionContent,
  CollectionViewMode,
  DYNAMIC_GALLERY_LOCAL_STORAGE_KEY,
  Navigation,
  RecordContent,
  RecordViewMode,
  RequestDataModel
} from '@aipb/gulliver/dynamic-gallery/model';
import { APP_NAME } from '@aipb/gulliver/env/util';
import { inject, Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { excludeKeys, localStorageStrategy, persistState } from '@ngneat/elf-persist-state';

interface DynamicGalleryProps {
  loading: boolean;
  records: RecordContent[];
  root: {
    collection: Collection;
    collections: CollectionContent[];
  };
  collection: Collection;
  collections: CollectionContent[];
  navigation: Navigation;
  castor: Castor;
  ui: {
    recordViewMode: RecordViewMode;
    collectionViewMode: CollectionViewMode;
    activeRecordId: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class DynamicGalleryRepository {
  store = createStore(
    { name: 'dynamic-gallery-config' },
    withProps<DynamicGalleryProps>({
      loading: false,
      collections: [],
      root: {
        collection: {
          url: '',
          description: '',
          label: '',
          contents: []
        },
        collections: []
      },
      records: [],
      collection: {
        url: '',
        description: '',
        label: '',
        contents: []
      },
      navigation: {
        breadcrumbs: [],
        pagination: {
          links: [],
          pageNum: 0,
          pagesCount: 0,
          pageSize: 0,
          recordsCount: 0
        }
      },
      castor: {
        url: '',
        coreId: '',
        uniqueIdName: ''
      },
      ui: {
        recordViewMode: 'masonry',
        collectionViewMode: 'masonry',
        activeRecordId: null
      }
    })
  );
  records$ = this.store.pipe(select((state) => state.records));
  rootCollection$ = this.store.pipe(select((state) => state.root.collection));
  rootCollections$ = this.store.pipe(select((state) => state.root.collections));
  collection$ = this.store.pipe(select((state) => state.collection));
  collections$ = this.store.pipe(select((state) => state.collections));
  breadcrumbs$ = this.store.pipe(select((state) => state.navigation.breadcrumbs));
  pagination$ = this.store.pipe(select((state) => state.navigation.pagination));
  collectionLabel$ = this.store.pipe(select((state) => state.collection.label));
  loading$ = this.store.pipe(select((state) => state.loading));
  activeRecordId$ = this.store.pipe(select((state) => state.ui.activeRecordId));
  recordViewMode$ = this.store.pipe(select((state) => state.ui.recordViewMode));
  collectionViewMode$ = this.store.pipe(select((state) => state.ui.collectionViewMode));
  castorUrl$ = this.store.pipe(select((state) => state.castor.url));
  coreId$ = this.store.pipe(select((state) => state.castor.coreId));
  private appName = inject(APP_NAME);
  persist = persistState(this.store, {
    key: `${this.appName}-${DYNAMIC_GALLERY_LOCAL_STORAGE_KEY}`,
    storage: localStorageStrategy,
    source: () =>
      this.store.pipe(excludeKeys(['loading', 'castor', 'records', 'navigation', 'collection', 'root', 'collections']))
  });

  setLoading(loading: boolean) {
    this.store.update((state) => ({
      ...state,
      loading
    }));
  }

  setRecordViewMode(recordViewMode: RecordViewMode) {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        recordViewMode
      }
    }));
  }

  setCollectionViewMode(collectionViewMode: CollectionViewMode) {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        collectionViewMode
      }
    }));
  }

  setActiveRecordId(activeRecordId: string) {
    this.store.update((state) => ({
      ...state,
      ui: {
        ...state.ui,
        activeRecordId
      }
    }));
  }

  setData(data: Partial<RequestDataModel>) {
    this.store.update((state) => ({
      ...state,
      ...data
    }));
  }

  reset() {
    this.store.update((state) => ({
      ...state,
      collections: [],
      records: [],
      navigation: {
        ...state.navigation,
        breadcrumbs: [
          {
            label: 'Domů',
            url: null
          }
        ]
      },
      castor: {
        url: '',
        coreId: '',
        uniqueIdName: ''
      }
    }));
  }
}
